import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

/**
 * The walkthrough's progress rail — `count` dots, one lit (specs/auth.md §5.4,
 * §12 #12).
 *
 * ---------------------------------------------------------------------------
 * DELIBERATELY NOT `kz-builder-step-tabs`.
 *
 * That component is a labelled, renameable, add/delete tab strip with four
 * outputs — an authoring control for a form builder. This is eight unlabelled
 * 4px rules that jump you to a slide. They look alike at a glance and share no
 * behaviour, and folding one into the other would mean giving the step tabs a
 * mode in which they have no labels and cannot be edited.
 * ---------------------------------------------------------------------------
 *
 * The source builds these at runtime — `slides.forEach((s,n) => …)` — so the
 * count follows the panes. Here it is an input for the same reason: the page
 * owns `WALKTHROUGH_PANE_COUNT`, and a dot rail that counted its own siblings
 * would be reading the DOM to learn something the page already knows.
 *
 * The dots ARE interactive, unlike everything in the panes: the source binds a
 * real `go(n)` handler to each. That is the distinction this feature keeps
 * making — a control gets a `<button>`, an illustration does not.
 */
@Component({
  selector: 'app-walkthrough-dots',
  standalone: false,
  templateUrl: './walkthrough-dots.component.html',
  styleUrls: ['./walkthrough-dots.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalkthroughDotsComponent {
  /** How many panes there are. Eight. */
  @Input() count = 0;

  /** Zero-based index of the pane on screen. */
  @Input() current = 0;

  /**
   * Accessible names, one per dot, already translated by the page.
   *
   * The source ships `aria-label="Screen 1".."Screen 8"` in ENGLISH even for a
   * French reader (spec §8, §11 R7). The port translates them — a deliberate
   * improvement, consistent with the five labels Login and Signup translated —
   * and the strings arrive from the page rather than being resolved here, so a
   * caller with different wording is not blocked by this component.
   *
   * Falls back to no `aria-label` rather than to an English default: a dot with
   * the wrong language announced is worse than one a screen reader describes by
   * position, and a silent English default is how the source's defect happened.
   */
  @Input() labels: readonly string[] = [];

  /** Emits the zero-based index the user asked for. */
  @Output() jump = new EventEmitter<number>();

  /** `[0 … count-1]`, so the template can `@for` without an index helper. */
  get indices(): readonly number[] {
    return Array.from({ length: this.count }, (_, n) => n);
  }

  labelFor(index: number): string | null {
    return this.labels[index] ?? null;
  }
}
